import { useEffect, useState } from "react";
import { dashboardApi } from "../../api/dashboardApi";
import { getCarelDiag, type CarelDiagResponse } from "../../api/carelApi";
import { useAuth } from "../../auth/AuthContext";

export function CarelTechnicalDetailPanel() {
  const { token } = useAuth() as any;
  const [machines, setMachines] = useState<Array<{ id: string; name?: string }>>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [diag, setDiag] = useState<CarelDiagResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    dashboardApi
      .getMachinesByProvider(token, "IFI_GALILEO")
      .then((m: any) => {
        const arr = Array.isArray(m) ? m : [];
        setMachines(arr);
        if (arr.length > 0)
          setSelectedId((prev) => (prev && arr.some((x: any) => x.id === prev) ? prev : arr[0].id));
      })
      .catch(() => setMachines([]));
  }, [token]);

  useEffect(() => {
    if (!token || !selectedId) {
      setDiag(null);
      return;
    }
    setLoading(true);
    setError(null);
    getCarelDiag(token, selectedId)
      .then(setDiag)
      .catch((e) => {
        setError(e?.message ?? "Errore caricamento diagnostica Carel");
        setDiag(null);
      })
      .finally(() => setLoading(false));
  }, [token, selectedId]);

  return (
    <div className="ifi-card">
      <div className="ifi-card-header">
        <i className="bi bi-cpu-fill" />
        Dettaglio tecnico Carel
      </div>
      <div className="ifi-chart-container" style={{ flexDirection: "column", alignItems: "stretch" }}>
        <div className="mb-3">
          <label className="form-label small text-muted text-uppercase fw-semibold">
            Macchina
          </label>
          <select
            className="form-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">— Seleziona macchina —</option>
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ?? m.id}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="ifi-loading py-4">
            <div className="ifi-loading-spinner" />
            <p className="ifi-loading-text">Caricamento variabili dal controller…</p>
          </div>
        )}

        {error && (
          <div className="ifi-error-card">
            <div className="ifi-error-icon">
              <i className="bi bi-exclamation-triangle-fill" />
            </div>
            <div>
              <div className="ifi-error-title">Errore</div>
              <p className="ifi-error-message">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && diag && (
          <div className="ifi-diag-content">
            <div className="small text-muted mb-2">
              Ultimo aggiornamento: {new Date(diag.fetchedAt).toLocaleString("it-IT")}
            </div>

            {diag.displayCases.length > 0 && (
              <div className="ifi-diag-section mb-4">
                <h6 className="ifi-diag-section-title">
                  <i className="bi bi-thermometer-half me-1" />
                  Vetrine
                </h6>
                <div className="row g-2">
                  {diag.displayCases.map((dc) => (
                    <div key={dc.displayCase} className="col-12 col-md-6 col-lg-4">
                      <div className="ifi-diag-vetrina">
                        <div className="ifi-diag-vetrina-num">Vetrina {dc.displayCase}</div>
                        <div className="ifi-diag-vetrina-row">
                          <span className="text-muted">T°</span>
                          <span className="fw-bold">
                            {dc.temperature != null
                              ? `${dc.temperature.toFixed(1)} °C`
                              : "—"}
                          </span>
                        </div>
                        <div className="ifi-diag-vetrina-row">
                          <span className="text-muted">Setpoint</span>
                          <span>
                            {dc.setpoint != null ? `${dc.setpoint.toFixed(1)} °C` : "—"}
                          </span>
                        </div>
                        {dc.probes.length > 0 && (
                          <div className="ifi-diag-probes mt-1">
                            {dc.probes.slice(0, 3).map((p, i) => (
                              <div key={i} className="ifi-diag-probe">
                                <span className="text-muted small">{p.name}</span>
                                <span className="small">{p.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diag.alarms.length > 0 && (
              <div className="ifi-diag-section mb-4">
                <h6 className="ifi-diag-section-title">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Allarmi
                </h6>
                <div className="ifi-diag-list">
                  {diag.alarms.map((a, i) => (
                    <div key={i} className="ifi-diag-row ifi-diag-row-alarm">
                      <span className="ifi-diag-name">{a.name}</span>
                      <span className="ifi-diag-value">{a.value}</span>
                      {a.description && (
                        <span className="ifi-diag-desc small text-muted">{a.description}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diag.other.length > 0 && (
              <div className="ifi-diag-section mb-4">
                <h6 className="ifi-diag-section-title">
                  <i className="bi bi-sliders me-1" />
                  Altre variabili
                </h6>
                <div className="ifi-diag-other-grid">
                  {diag.other.slice(0, 24).map((o, i) => (
                    <div key={i} className="ifi-diag-other-item">
                      <span className="ifi-diag-other-name" title={o.description}>
                        {o.name}
                      </span>
                      <span className="ifi-diag-other-value">{o.value}</span>
                    </div>
                  ))}
                </div>
                {diag.other.length > 24 && (
                  <p className="small text-muted mt-2 mb-0">
                    + altre {diag.other.length - 24} variabili
                  </p>
                )}
              </div>
            )}

            {diag.allVariables && diag.allVariables.length > 0 && (
              <div className="ifi-diag-section mb-4">
                <h6 className="ifi-diag-section-title">
                  <i className="bi bi-table me-1" />
                  Tutte le variabili (getvar.csv)
                </h6>
                <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                  <table className="table table-sm table-striped ifi-diag-table">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Nome</th>
                        <th>ID</th>
                        <th>Descrizione</th>
                        <th>Tipo</th>
                        <th>Accesso</th>
                        <th>Valore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diag.allVariables.map((v, i) => (
                        <tr key={i}>
                          <td className="ifi-diag-var-name">{v.name}</td>
                          <td className="text-muted small">{v.id}</td>
                          <td className="ifi-diag-var-desc small" title={v.desc}>
                            {v.desc.length > 50 ? v.desc.slice(0, 50) + "…" : v.desc}
                          </td>
                          <td><span className="badge bg-secondary">{v.type}</span></td>
                          <td className="small">{v.access}</td>
                          <td className="ifi-diag-var-val fw-medium">{v.val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="small text-muted mt-2 mb-0">
                  {diag.allVariables.length} variabili dal controller Carel
                </p>
              </div>
            )}

            {diag.displayCases.length === 0 &&
              diag.alarms.length === 0 &&
              diag.other.length === 0 &&
              (!diag.allVariables || diag.allVariables.length === 0) && (
                <div className="ifi-chart-empty">
                  <i className="bi bi-inbox" />
                  <p>Nessuna variabile disponibile</p>
                </div>
              )}
          </div>
        )}

        {!loading && !error && !diag && selectedId && (
          <div className="ifi-chart-empty">
            <i className="bi bi-question-circle" />
            <p>Seleziona una macchina per visualizzare il dettaglio</p>
          </div>
        )}
      </div>
    </div>
  );
}
